import { useApolloClient } from "@apollo/client";
import { Formik } from "formik";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";
import { IWizardExecuteAllModel, IWizardStep, Wizard, WizardTypes } from "../../components/Wizard";
import { WizardWrapper } from "../../components/WizardWrapper";
import { AdvancedEmailWizardContext, AttachmentContextProvider } from "../../context";
import { EmailTemplateService } from "../../data/graphQL/services/EmailTemplateService";
import { IEmailTemplate } from "../../data/graphQL/types";
import { strings } from "../../localisation";
import { IEmailDataModel, mapTemplateToEmailDataModel } from "../EmailWizard/models";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import { Details } from "../Steps/Details/Details";
import { EmailContent } from "../Steps/EmailContent/EmailContent";
import { Preview } from "../Steps/Preview/Preview";
import { ITemplateDataModel } from "./models/Template";
import { TemplateExecuteModel } from "./models/Template/TemplateExecuteModel";
import { ThumbnailGenerator } from "./steps/ThumbnailGenerator";

interface ITemplateResponse {
    isLoading: boolean;
    data: IEmailTemplate;
}

interface IThumbnailOptions {
    html: string;
    onThumbnailGenerated: (thumbnailBase64: string | null) => Promise<void>;
}

export interface ITemplateWizardProps {
    TemplateID: number;
    OnWizardClose: () => void;
    Events: IAdvancedTemplateAppEvents;
}

export const TemplateWizard: React.FunctionComponent<ITemplateWizardProps> = (
    { TemplateID, OnWizardClose, Events }: ITemplateWizardProps,
) => {
    // Get GraphQL Client and construct EmailTemplateService
    const apolloClient = useApolloClient();
    const emailTemplateService = React.useMemo(() => new EmailTemplateService(apolloClient), []);

    // Refs
    const formikRef = React.useRef<Formik<IWizardExecuteAllModel>>(null);

    // Context
    const { AddressTypes, IsLoading } = React.useContext(AdvancedEmailWizardContext);

    // State
    const [, setIsClosing] = React.useState(false);
    const [temporaryAttachmentFolder] = React.useState(`_attachments_${uuidv4()}`);
    const [template, setTemplate] = React.useState<ITemplateResponse>({
        isLoading: TemplateID >= 0,
        data:
        {
            id: TemplateID,
            templateName: "",
            templateDetails: {
                emailBody: "",
                unlayerJsonString: "{}",
                correspondenceName: "",
                emailSubject: "",
                emailTo: [],
                emailFromID: -1,
                emailCC: false,
                emailCCAddress: [],
                emailBcc: false,
                emailBccAddress: [],
                cCAddressOnlyFirst: true,
                bCCAddressOnlyFirst: true,
                viewOnWeb: false,
                copyToCorrespondence: false,
                temporaryAttachmentFolder,
            },
            templateAttachments: [],
        },
    });

    const [thumbnailOptions, setThumbnailOptions] = React.useState<IThumbnailOptions>();

    // Get template
    if (template.isLoading) {
        emailTemplateService.get(TemplateID)
            .then((emailTemplate?: IEmailTemplate) => {
                if (emailTemplate) {

                    const templateData = emailTemplate.templateDetails ?? template.data.templateDetails;

                    setTemplate({
                        isLoading: false,
                        data: {
                            id: emailTemplate.id,
                            templateName: emailTemplate.templateName,
                            templateDetails: {
                                ...templateData,
                                temporaryAttachmentFolder,
                            },
                            templateAttachments: emailTemplate.templateAttachments,
                        },
                    });
                } else {
                    setTemplate({
                        ...template,
                        isLoading: false,
                    });
                }
            });
    }

    // Data
    const executeData: ITemplateDataModel = {
        ...mapTemplateToEmailDataModel(template.data, AddressTypes!),
        TemplateName: template.data.templateName,
        TemplateThumbnailBase64: null,
    };

    const srExecuteWizardModel: TemplateExecuteModel = new TemplateExecuteModel(TemplateID, executeData);

    const steps: IWizardStep[] = [
        {
            NextLabel: strings.getString(`${WizardTypes.Template}_Action_Wizard_Next_Content`),
            TrackerProps: {
                ClientId: "step1-tab",
                AriaControlsPanel: "step1-panel",
                Title: strings.Label_Step_TemplateDetails_Title,
                AriaLabel: strings.Label_Step_TemplateDetails_AriaLabel,
            },
            TrackerPanel:
                <Details
                    FormikValues={srExecuteWizardModel}
                    ValidationSchema={Yup.object().shape({
                        formDataModel: Yup.object({
                            TemplateName: Yup.string().trim().required().max(50).label(strings.Label_Template_Name),
                            CorrespondenceName: Yup.string().trim().label(strings.Label_Email_Name),
                            EmailSubject: Yup.string().trim().label(strings.Label_Email_Subject),
                            EmailToItems: Yup.array().nullable().label(strings.Label_Email_RecipientAddress),
                            EmailFromItem: Yup.object().nullable().label(strings.Label_Email_FromAddress),
                            CCAddressOnlyFirst: Yup.boolean(),
                            BCCAddressOnlyFirst: Yup.boolean(),
                            EmailCC: Yup.boolean(),
                            EmailCCAddressItems: Yup.array().when(nameof<TemplateExecuteModel>((m) => m.formDataModel.EmailCC), {
                                is: (value) => value === true, then: () => {
                                    return Yup.array().required().nullable().label(strings.Label_Email_CC);
                                },
                            }),
                            EmailBCC: Yup.boolean(),
                            EmailBCCAddressItems: Yup.array().when(nameof<TemplateExecuteModel>((m) => m.formDataModel.EmailBCC), {
                                is: (value) => value === true, then: () => {
                                    return Yup.array().required().nullable().label(strings.Label_Email_BCC);
                                },
                            }),
                            ViewOnWeb: Yup.boolean(),
                        }),
                    })} />,
        },
        {
            NextLabel: strings.getString(`${WizardTypes.Template}_Action_Wizard_Next_Preview`),
            PreviousLabel: strings.getString(`${WizardTypes.Template}_Action_Wizard_Prev_Email`),
            TrackerProps: {
                ClientId: "step2-tab",
                AriaControlsPanel: "step2-panel",
                Title: strings.Label_Step_TemplateContent_Title,
                AriaLabel: strings.Label_Step_TemplateContent_AriaLabel,
            },
            TrackerPanel:
                <EmailContent
                    FormikValues={srExecuteWizardModel}
                    ValidationSchema={Yup.object().shape({
                        formDataModel: Yup.object({
                            EmailBody: Yup.string(), // TODO: this can be strongly typed if we break up the ISendEmailExecuteModel in to models per step
                        }),
                    })}
                    ShouldValidate={false} />,
        },
        {
            PreviousLabel: strings.getString(`${WizardTypes.Template}_Action_Wizard_Prev_Content`),
            TrackerProps: {
                ClientId: "step3-tab",
                AriaControlsPanel: "step3-panel",
                Title: strings.Label_Step_TemplatePreview_Title,
                AriaLabel: strings.Label_Step_TemplatePreview_AriaLabel,
            },
            TrackerPanel:
                <Preview
                    FormikValues={srExecuteWizardModel}
                    ValidationSchema={Yup.object()} />,
        },
    ];

    // Event Handlers
    const saveTemplateOnComplete = async (model: IWizardExecuteAllModel): Promise<void> => {
        model.formDataModel.transformData();
        const saveModel: ITemplateDataModel = model.formDataModel as ITemplateDataModel;

        // Has the email body changed?
        const initialEmailDataModel = formikRef.current!.initialValues.formDataModel as IEmailDataModel;
        const emailBodyChanged = initialEmailDataModel.EmailBody !== saveModel.EmailBody;

        if (emailBodyChanged) {
            return new Promise((resolve, reject) => {
                const savePromise = (thumbnailBase64: string | null) => {

                    // Update thumbnail from callback data
                    saveModel.TemplateThumbnailBase64 = thumbnailBase64;

                    return emailTemplateService.save(TemplateID, saveModel)
                        .then(() => {

                            // Fire On complete in StarRez
                            Events.handleOnTemplateSaved(model.id!, saveModel.TemplateName);

                            // Set Template Data
                            setTemplate({
                                isLoading: false,
                                data: {
                                    ...template.data,
                                    templateName: saveModel.TemplateName,
                                },
                            });

                            // Reset thumbnail generation
                            setThumbnailOptions(undefined);

                            resolve();
                        }).catch((error) => {
                            reject(error);
                            throw new Error(error);
                        });
                };

                setThumbnailOptions({
                    html: saveModel.EmailBody,
                    onThumbnailGenerated: savePromise,
                });
            });
        } else {

            // Reset thumbnail
            saveModel.TemplateThumbnailBase64 = null;

            return emailTemplateService.save(TemplateID, saveModel)
                .then(() => {

                    // Fire On complete in StarRez
                    Events.handleOnTemplateSaved(model.id!, saveModel.TemplateName);

                    // Set Template Data
                    setTemplate({
                        isLoading: false,
                        data: {
                            ...template.data,
                            templateName: saveModel.TemplateName,
                        },
                    });

                });
        }
    };

    const handleOnClose = (shouldClose: boolean) => {
        if (shouldClose) {
            setIsClosing(false);
            OnWizardClose();
        } else {
            setIsClosing(false);
        }
    };

    const getFormikDirty = (): boolean => formikRef.current?.getFormikBag().dirty || false;

    return (
        <AttachmentContextProvider
            AttachmentFolder={executeData.TemporaryAttachmentFolder ?? ""}
            AttachmentEvents={Events}
            InitialAttachments={executeData.Attachments}
        >
            <WizardWrapper
                CloseAriaLabel={strings.Action_CloseTemplateWizard}
                CloseTitle={strings.Action_CloseTemplateWizard}
                Header={strings.formatString(strings.Message_Wizard_TemplateToHeader, template.data.templateName) as string}
                OnCloseConfirm={() => OnWizardClose()}
                ShouldPromptSave={true}
                IsDirty={getFormikDirty}
            >
                <ErrorBoundary>
                    <Wizard
                        Steps={steps}
                        OnComplete={saveTemplateOnComplete}
                        OnCloseRequest={handleOnClose}
                        WizardType={WizardTypes.Template}
                        IsLoading={template.isLoading || IsLoading}
                        FormikRef={formikRef}
                    />
                </ErrorBoundary>

            </WizardWrapper>

            <ThumbnailGenerator
                Html={thumbnailOptions?.html || null}
                OnThumbnailGenerated={thumbnailOptions?.onThumbnailGenerated}
                TemplateEvents={Events}
            />

        </AttachmentContextProvider>
    );
};
